import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  MenuItem,
  TextField,
  Select,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  FormHelperText,
} from "@mui/material";

const StepPayment = ({
  formData,
  setFormData,
  errors,
  updatePaymentSummary,
}) => {
  useEffect(() => {
    const subtotal = formData.items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unitPrice) || 0;
      return sum + qty * price;
    }, 0);

    const tax = 0; // can be updated later
    const deliveryFee = formData.fulfillment.method === "delivery" ? 9.95 : 0;
    const total = parseFloat((subtotal + tax + deliveryFee).toFixed(2));

    setFormData((prev) => ({
      ...prev,
      payment: {
        ...prev.payment,
        subtotal,
        tax,
        deliveryFee,
        total,
      },
    }));
  }, [formData.items, formData.fulfillment.method]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;

    setFormData((prev) => {
      const updated = {
        ...prev,
        payment: {
          ...prev.payment,
          [field]: value,
        },
      };

      return updated;
    });
  };

  const handleNestedChange = (nestedField) => (e) => {
    setFormData((prev) => ({
      ...prev,
      payment: {
        ...prev.payment,
        paymentMethod: {
          ...prev.payment.paymentMethod,
          [nestedField]: e.target.value,
        },
      },
    }));
  };

  const togglePaid = (e) => {
    setFormData((prev) => ({
      ...prev,
      payment: {
        ...prev.payment,
        paid: e.target.checked,
      },
    }));
  };

  const payment = formData.payment || {};
  const methodType = payment.paymentMethod?.type || "";

  return (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Payment Info
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Select
            fullWidth
            value={payment.paymentStatus || ""}
            onChange={handleChange("paymentStatus")}
            displayEmpty
            error={!!errors?.paymentStatus}>
            <MenuItem value="" disabled>
              Select Payment Status
            </MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="succeeded">Succeeded</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="refunded">Refunded</MenuItem>
            <MenuItem value="partial_refund">Partial Refund</MenuItem>
          </Select>
          {errors?.paymentStatus && (
            <FormHelperText error>{errors.paymentStatus}</FormHelperText>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Currency"
            fullWidth
            value={payment.currency || "CAD"}
            onChange={handleChange("currency")}
            error={!!errors?.currency}
            helperText={errors?.currency || ""}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch checked={payment.paid || false} onChange={togglePaid} />
            }
            label="Mark as Paid"
          />
        </Grid>

        <Grid item xs={12}>
          <Select
            fullWidth
            value={methodType}
            onChange={handleNestedChange("type")}
            displayEmpty
            error={!!errors?.paymentMethod}>
            <MenuItem value="" disabled>
              Select Payment Method
            </MenuItem>
            <MenuItem value="stripe">Stripe</MenuItem>
            <MenuItem value="in-store">In-store</MenuItem>
            <MenuItem value="etransfer">E-transfer</MenuItem>
          </Select>
          {errors?.paymentMethod && (
            <FormHelperText error>{errors.paymentMethod}</FormHelperText>
          )}
        </Grid>

        {methodType === "stripe" && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography fontWeight="bold" gutterBottom>
                  Stripe Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Payment Intent ID"
                      fullWidth
                      value={payment.stripePaymentIntent || ""}
                      onChange={handleChange("stripePaymentIntent")}
                      error={!!errors?.stripePaymentIntent}
                      helperText={errors?.stripePaymentIntent || ""}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Receipt URL"
                      fullWidth
                      value={payment.stripeReceiptUrl || ""}
                      onChange={handleChange("stripeReceiptUrl")}
                      error={!!errors?.stripeReceiptUrl}
                      helperText={errors?.stripeReceiptUrl || ""}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Receipt Number"
                      fullWidth
                      value={payment.receiptNumber || ""}
                      onChange={handleChange("receiptNumber")}
                      error={!!errors?.receiptNumber}
                      helperText={errors?.receiptNumber || ""}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {methodType === "in-store" && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography fontWeight="bold" gutterBottom>
                  In-Store Payment Details
                </Typography>
                <TextField
                  label="POS Receipt Number"
                  fullWidth
                  value={payment.receiptNumber || ""}
                  onChange={handleChange("receiptNumber")}
                  error={!!errors?.receiptNumber}
                  helperText={errors?.receiptNumber || ""}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {methodType === "etransfer" && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography fontWeight="bold" gutterBottom>
                  E-transfer Details
                </Typography>
                <TextField
                  label="Reference Number"
                  fullWidth
                  value={payment.receiptNumber || ""}
                  onChange={handleChange("receiptNumber")}
                  error={!!errors?.receiptNumber}
                  helperText={errors?.receiptNumber || ""}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} sm={4}>
          <TextField
            label="Subtotal"
            type="number"
            value={payment.subtotal || ""}
            fullWidth
            onChange={(e) =>
              setFormData((prev) => {
                const newSubtotal = parseFloat(e.target.value || 0);
                const tax = prev.payment.tax || 0;
                const deliveryFee =
                  prev.fulfillment.method === "delivery" ? 9.95 : 0;
                const total = parseFloat(
                  (newSubtotal + tax + deliveryFee).toFixed(2)
                );

                return {
                  ...prev,
                  payment: {
                    ...prev.payment,
                    subtotal: newSubtotal,
                    total,
                  },
                };
              })
            }
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="Tax"
            type="number"
            value={payment.tax || ""}
            fullWidth
            onChange={(e) =>
              setFormData((prev) => {
                const tax = parseFloat(e.target.value || 0);
                const subtotal = prev.payment.subtotal || 0;
                const deliveryFee =
                  prev.fulfillment.method === "delivery" ? 9.95 : 0;
                const total = parseFloat(
                  (subtotal + tax + deliveryFee).toFixed(2)
                );

                return {
                  ...prev,
                  payment: {
                    ...prev.payment,
                    tax,
                    total,
                  },
                };
              })
            }
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="Total"
            value={payment.total?.toFixed(2) || "0.00"}
            fullWidth
            InputProps={{ readOnly: true }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default StepPayment;
